/**
 * DegradedBanner component.
 *
 * Warning banner shown when the queue API returns degraded: true.
 * Renders a persistent warning above the list when some approval
 * sources are temporarily unavailable.
 */

import type { CSSProperties } from 'react'
import { AlertTriangle } from 'lucide-react'

export interface DegradedBannerProps {
  /** Which sources are unavailable. */
  sources?: string[]
}

/**
 * Warning banner shown when the queue API returns degraded: true.
 */
export function DegradedBanner({ sources }: DegradedBannerProps) {
  return (
    <div role="alert" style={styles.container}>
      <AlertTriangle style={styles.icon} />
      <div style={styles.content}>
        <p style={styles.title}>Some items are temporarily unavailable.</p>
        <p style={styles.description}>
          {sources && sources.length > 0 ? `Affected sources: ${sources.join(', ')}. ` : ''}
          Retrying automatically.
        </p>
      </div>
    </div>
  )
}

const styles: Record<string, CSSProperties> = {
  container: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px 16px',
    borderRadius: 'var(--radius-lg)',
    border: '1px solid rgba(245, 158, 11, 0.3)',
    background: 'rgba(245, 158, 11, 0.1)',
    fontSize: '14px',
  },
  icon: {
    width: '20px',
    height: '20px',
    flexShrink: 0,
    color: 'var(--warning)',
  },
  content: {
    flex: 1,
  },
  title: {
    fontWeight: 500,
    color: 'var(--text-primary)',
  },
  description: {
    color: 'var(--text-muted)',
  },
}

export default DegradedBanner
